import * as React from "react"
import {Slot} from "@radix-ui/react-slot"
import {cva} from "class-variance-authority";

import {cn} from "@/lib/utils"

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-x-2 whitespace-nowrap rounded-md font-medium transition focus-visible:outline-none hover:cursor-pointer disabled:ring-none disabled:pointer-not-allowed disabled:opacity-50 disabled:hover:cursor-not-allowed",
    {
        variants: {
            variant: {
                default: "bg-violet-950 text-white hover:bg-violet-900 hover:ring-2 hover:ring-violet-200 hover:ring-offset-1 hover:ring-offset-white disabled:hover:ring-0 disabled:hover:ring-offset-0",
                destructive: "bg-red-500 text-white border border-red-500 hover:bg-red-600 hover:ring-2 hover:ring-red-200 hover:ring-offset-1 hover:ring-offset-white",
                secondary: "bg-gray-50 text-gray-900 hover:bg-gray-100",
                ghost: "hover:bg-accent hover:text-accent-foreground hover:ring-2 hover:ring-gray-200 hover:ring-offset-1 hover:ring-offset-white",
                link: "text-primary underline-offset-4 hover:underline",
                info: "bg-blue-500 text-white hover:bg-blue-600 hover:ring-2 hover:ring-blue-300 hover:ring-offset-1 hover:ring-offset-white",
                danger: "bg-red-400 text-white rounded-md px-1.5 transition hover:bg-red-600 hover:ring-2 hover:ring-red-100 focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2",
                success: "bg-emerald-500 text-white hover:bg-emerald-600 hover:ring-2 hover:ring-emerald-300 hover:ring-offset-1 hover:ring-offset-white",
            },
            size: {
                default: "rounded-md text-sm px-3 py-1.5",
                xxs: "h-5 rounded text-xs px-1",
                xs: "rounded text-xs px-2 py-1",
                sm: "rounded-md text-sm px-4 h-8",
                md: "rounded-md text-base px-4 py-2",
                lg: "h-11 rounded-md px-8",
                icon: "h-8 w-8",
                'icon-sm': "h-6 w-6",
                'icon-xs': "h-5 w-5",
            },
            outline: {
                true: 'bg-transparent border',
            },
        },
        compoundVariants: [
            {
                variant: 'default',
                outline: true,
                className: 'text-violet-900 border border-violet-200 bg-violet-50 hover:bg-violet-50 hover:ring-2 hover:ring-offset-1 hover:ring-offset-white hover:ring-violet-200 hover:text-violet-950'
            },
            {
                variant: 'destructive',
                outline: true,
                className: 'border-transparent text-red-500 hover:bg-red-50 hover:text-red-600 hover:ring-2 hover:ring-offset-1 hover:ring-offset-white hover:ring-red-200'
            },
            {
                variant: 'secondary',
                outline: true,
                className: 'border-gray-200 text-gray-700 hover:bg-gray-100 hover:text-gray-800'
            },
            {
                variant: 'ghost',
                outline: true,
                className: 'bg-white border-gray-200 text-gray-600 hover:text-gray-900 hover:ring-2 hover:ring-offset-1 hover:ring-offset-white hover:ring-gray-200'
            },
            {
                variant: 'link',
                outline: true,
                className: 'border-primary-200 text-primary hover:underline'
            },
            {
                variant: 'info',
                outline: true,
                className: 'border-blue-500 text-blue-500 hover:bg-blue-50 hover:text-blue-600'
            },
            {
                variant: 'danger',
                outline: true,
                className: 'bg-red-50 border-red-500 text-red-500 hover:bg-red-500 hover:text-white hover:ring-2 hover:ring-offset-1 hover:ring-offset-white hover:ring-red-200'
            },
            {
                variant: 'success',
                outline: true,
                className: 'border-transparent text-emerald-600 hover:bg-emerald-50/50 hover:text-emerald-600 hover:border-emerald-400 hover:ring-2 hover:ring-offset-1 hover:ring-offset-white hover:ring-emerald-200'
            },
            {
                variant: 'destructive-ghost',
                outline: true,
                className: 'bg-white border-transparent text-red-500 hover:bg-red-50 hover:text-red-600 hover:ring-2 hover:ring-offset-1 hover:ring-offset-white hover:ring-red-200'
            },
        ],
        defaultVariants: {
            variant: "default",
            size: "default",
            outline: false,
        },
    }
)

const Button = React.forwardRef(({
                                     className,
                                     variant,
                                     size,
                                     outline = false,
                                     asChild = false,
                                     ...props
                                 }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
        (<Comp
            className={cn(buttonVariants({variant, size, outline, className}))}
            ref={ref}
            {...props} />)
    );
})
Button.displayName = "Button"

export {Button, buttonVariants}
